import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, IconButton, Popover, Box, Typography, Stack, CircularProgress, ButtonBase, Tooltip } from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { notificationsApi } from '../../services/api';
import { apiError } from '../../utils/format';
import { tokens } from '../../theme';

const ICON = {
  error: <ErrorOutlineRoundedIcon sx={{ color: tokens.error }} />,
  warning: <WarningAmberRoundedIcon sx={{ color: tokens.warning }} />,
  info: <InfoOutlinedIcon sx={{ color: tokens.info }} />,
};

// Notifications are derived on the server from real states: sync problems, missing inputs.
const NotificationsButton = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await notificationsApi.list();
      setItems(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const important = (items || []).filter((n) => n.severity !== 'info').length;

  return (
    <>
      <Tooltip title="Уведомления">
        <IconButton
          aria-label={`Уведомления${items ? `: ${items.length}` : ''}`}
          onClick={(e) => {
            setAnchor(e.currentTarget);
            load();
          }}
          sx={{ width: 48, height: 48, border: `1px solid ${tokens.border}`, bgcolor: tokens.paper, borderRadius: 3 }}
        >
          <Badge color="error" variant={important ? 'standard' : 'dot'} badgeContent={important || (items?.length ? 1 : 0)} invisible={!items?.length}>
            <NotificationsNoneRoundedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 32px)', maxHeight: 480 } } }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.border}` }}>
          <Typography variant="subtitle1">Уведомления</Typography>
        </Box>
        {!items && !error && (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        )}
        {error && <Typography sx={{ p: 2 }} color="error" variant="body2">{error}</Typography>}
        {items && items.length === 0 && (
          <Typography sx={{ p: 3 }} color="text.secondary" variant="body2">Новых уведомлений нет. Проблем с загрузкой данных не обнаружено.</Typography>
        )}
        <Stack>
          {(items || []).map((n) => (
            <ButtonBase
              key={n.id}
              onClick={() => {
                setAnchor(null);
                if (n.link) navigate(n.link);
              }}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, textAlign: 'left', borderBottom: `1px solid ${tokens.border}`, '&:hover': { bgcolor: tokens.primaryTint } }}
            >
              {ICON[n.severity] || ICON.info}
              <Box>
                <Typography variant="subtitle2">{n.title}</Typography>
                <Typography variant="body2" color="text.secondary">{n.text}</Typography>
              </Box>
            </ButtonBase>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default NotificationsButton;
